import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ChevronUpDownIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import styled, { css } from 'styled-components';

import { Environment } from 'dashboard/models/environment';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import MarkPapaya from 'images/brand/mark-papaya-700.svg';
import MarkUbe from 'images/brand/mark-ube-500.svg';
import { ApiModel } from 'lib/interfaces/api';

interface EnvironmentPickerDropdownProps {
  environments: Environment[];
  currentEnvironment: Environment | undefined;
  canCreateEnvironments: boolean;
  handleSwitchEnvironment: (environement: Environment) => void;
  handleCreateNewEnvironment: () => void;
}

const EnvironmentPickerDropdown = ({
  currentEnvironment,
  environments,
  canCreateEnvironments,
  handleCreateNewEnvironment,
  handleSwitchEnvironment,
}: EnvironmentPickerDropdownProps) => {
  return (
    <EnvironmentDropdownMenu
      side='right'
      portal={true}
      // Make sure content is higher z-index than CollapseIcon
      contentProps={{ sideOffset: -10, style: { zIndex: Z_INDICES.modalElevated + 1 } }}
      trigger={
        <EnvironmentDisplayButton $isHeaderMenuItem={true} id='navbar__environment'>
          <EnvironmentDisplay environment={currentEnvironment} />
          <ChevronUpDownIcon style={{ marginLeft: 'auto' }} />
        </EnvironmentDisplayButton>
      }
    >
      {environments.map((env) => {
        return (
          <EnvironmentMenuItem key={env.id}>
            <EnvironmentDisplayButton
              id={`navbar__header-environment--${env.id}`}
              onClick={() => {
                handleSwitchEnvironment(env);
              }}
            >
              <EnvironmentDisplay environment={env} />
            </EnvironmentDisplayButton>
          </EnvironmentMenuItem>
        );
      })}
      {canCreateEnvironments && (
        <Button
          id='environment_header__create'
          key='create'
          prefixIcon={<PlusIcon />}
          variant='inverse'
          onClick={handleCreateNewEnvironment}
        >
          Create new
        </Button>
      )}
    </EnvironmentDropdownMenu>
  );
};

const EnvironmentDisplay = ({ environment }: { environment: Environment | undefined }) => {
  return (
    <EnvironmentDisplayWrapper>
      {environment && (
        <>
          <EnvironmentMark>
            {environment.type === ApiModel.EnvironmentProduction ? (
              <MarkUbe height={32} width={32} />
            ) : (
              <MarkPapaya height={32} width={32} />
            )}
          </EnvironmentMark>
          <div>
            <span>{environment.attributes.name}</span>
            {environment.type !== ApiModel.EnvironmentProduction && (
              <SandboxLabel>Simulated Data</SandboxLabel>
            )}
          </div>
        </>
      )}
    </EnvironmentDisplayWrapper>
  );
};

const EnvironmentDropdownMenu = styled(DropdownMenu)`
  max-height: 500px;
  overflow-y: scroll;
`;

const EnvironmentDisplayButton = styled.button<{
  $isHeaderMenuItem?: boolean;
}>`
  height: 48px;
  background-color: transparent;
  border-radius: ${({ theme }) => theme.button.borderRadius};
  display: flex;
  width: 100%;
  padding: ${Spacing[2]};
  grid-gap: ${Spacing[2]};
  align-items: center;
  overflow: hidden;

  &:hover {
    cursor: pointer;
    span {
      color: ${Color.TextActive};
    }
  }

  ${({ $isHeaderMenuItem }) => {
    if ($isHeaderMenuItem) {
      return css`
        padding: ${Spacing[2]};
        &:hover {
          background-color: ${({ theme }) => theme.color.background.highlight};
        }
      `;
    }
  }}
`;

const EnvironmentMenuItem = styled(DropdownMenu.Item)`
  width: 100%;

  li {
    width: 100%;
  }

  &:hover,
  &:active,
  &:focus {
    button:not(:disabled) svg * {
      stroke: unset;
    }

    button {
      span {
        color: ${Color.TextActive};
      }
    }
  }
`;

const SandboxLabel = styled.div`
  ${Typography.TextSmall};
  color: ${Color.CoreOrange600};
`;

const EnvironmentDisplayWrapper = styled.span`
  display: flex;
  text-align: left;
  grid-gap: ${Spacing[3]};
  align-items: center;
`;

const EnvironmentMark = styled.div`
  flex-shrink: 0;
`;

export default EnvironmentPickerDropdown;
